import {
  Checkbox,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from "@fluentui/react-components"
import type { RolePage } from "@rema/shared/schemas/roles"
import type { AdminUser } from "@rema/shared/schemas/users"
import { useQuery } from "@tanstack/react-query"
import { type FormEvent, useEffect, useState } from "react"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { api } from "../../lib/api"
import { usersI18n as i18n } from "./i18n"

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
})

export interface RolesDrawerProps {
  open: boolean
  user: AdminUser | null
  onClose: () => void
  onSave: (id: string, roleIds: string[]) => Promise<unknown>
}

// Asignación de roles del usuario (PUT /admin/users/:id/roles). Lista los roles del
// alcance (globales + del dominio) y preselecciona los que el usuario ya tiene.
export const RolesDrawer = ({ open, user, onClose, onSave }: RolesDrawerProps) => {
  const styles = useStyles()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)

  const rolesQuery = useQuery({
    queryKey: ["admin", "roles", "picker", user?.domain_id ?? null],
    queryFn: () =>
      api.get<RolePage>("/admin/roles", {
        // include_global: un usuario del dominio puede recibir tanto roles del dominio
        // como los globales/de sistema (p. ej. "user"). Sin esto, un dominio sin roles
        // propios mostraría la lista vacía. El maestro ya recibe ambos por su token.
        query: { limit: 100, domain: user?.domain ?? undefined, include_global: true },
      }),
    enabled: open && user !== null,
  })

  const roles = rolesQuery.data?.data ?? []

  // Al abrir (o al cargar los roles), preselecciona por nombre los que ya tiene el usuario.
  // Se depende de rolesQuery.data (referencia estable de React Query), NO de `roles`
  // (= `data ?? []`, que crea un array nuevo en cada render mientras carga → bucle
  // infinito de setState → re-render → efecto, React error #185).
  useEffect(() => {
    if (!open || !user) return
    const owned = new Set(user.roles)
    const list = rolesQuery.data?.data ?? []
    setSelected(new Set(list.filter((r) => owned.has(r.name)).map((r) => r.id)))
  }, [open, user, rolesQuery.data])

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!user) return
    setSubmitting(true)
    try {
      await onSave(user.id, [...selected])
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppDrawerForm
      open={open}
      title={i18n.rolesDrawer.title}
      submitLabel={i18n.rolesDrawer.submit}
      submitting={submitting}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {rolesQuery.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>{i18n.rolesDrawer.loadError}</MessageBarBody>
        </MessageBar>
      ) : roles.length === 0 ? (
        <MessageBar intent="info">
          <MessageBarBody>{i18n.rolesDrawer.empty}</MessageBarBody>
        </MessageBar>
      ) : (
        <div className={styles.list}>
          {roles.map((role) => (
            <Checkbox
              key={role.id}
              label={role.name}
              checked={selected.has(role.id)}
              onChange={(_, data) => toggle(role.id, Boolean(data.checked))}
            />
          ))}
        </div>
      )}
    </AppDrawerForm>
  )
}
